/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useContext} from 'react'
import PropTypes from 'prop-types'
import {useIntl, defineMessage, FormattedMessage} from 'react-intl'
import {Box, Container, Heading, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerBaskets,
    useCustomerId,
    useCustomerType,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import {useForm} from 'react-hook-form'
import {useRouteMatch} from 'react-router'
import {useLocation, useHistory} from 'react-router-dom'
import useEinstein from '../../hooks/use-einstein'
import LoginForm from '../../components/login'
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index'
import {
    API_ERROR_MESSAGE,
    CREATE_ACCOUNT_FIRST_ERROR_MESSAGE,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_LOGIN_LANDING_PATH,
    PASSWORDLESS_ERROR_MESSAGES,
    USER_NOT_FOUND_ERROR,
    OUTLET_DISPATCH_URL
} from '../../constants'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {IPContext} from '../../custom-contexts'
import {removePrefixFromKeys} from './../../utils/utils'
import useMultiSite from '../../hooks/use-multi-site'
import {useXSSProtection} from './../../hooks/use-xss-protection.js'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import useUpdateShopperContext from '@app/hooks/use-update-shopper-context'

const LOGIN_ERROR_MESSAGE = defineMessage({
    defaultMessage: 'Something went wrong. Try again.',
    id: 'login_page.error.incorrect_username_or_password'
})

const LOGIN_VIEW = 'login'
const EMAIL_VIEW = 'email'

const Login = ({initialView = LOGIN_VIEW}) => {
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const form = useForm()
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const {path} = useRouteMatch()
    const einstein = useEinstein()
    const {isRegistered, customerType} = useCustomerType()
    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)
    const loginPasswordless = useAuthHelper(AuthHelpers.LoginPasswordlessUser)
    const authorizePasswordlessLogin = useAuthHelper(AuthHelpers.AuthorizePasswordless)
    const {passwordless = {}, social = {}} = getConfig().app.login || {}
    const isPasswordlessEnabled = !!passwordless?.enabled
    const isSocialEnabled = !!social?.enabled
    const idps = social?.idps

    const customerId = useCustomerId()
    const prevAuthType = usePrevious(customerType)
    const {data: baskets, isSuccess: isSuccessCustomerBaskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const [currentView, setCurrentView] = useState(initialView)
    const [passwordlessLoginEmail, setPasswordlessLoginEmail] = useState('')
    const [loginType, setLoginType] = useState(LOGIN_TYPES.PASSWORD)
    const [redirectPath, setRedirectPath] = useState('')

    const {isIPBlocked} = useContext(IPContext)
    const loginFormPrefix = 'login_'
    const {buildUrl, isOutletSite} = useMultiSite()
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()
    const {triggerLoginEvent} = useGTM()
    const {updateShopperContextAfterLogout} = useUpdateShopperContext()

    const handleMergeBasket = () => {
        const hasBasketItem = baskets?.baskets?.[0]?.productItems?.length > 0
        // we only want to merge basket when the user is logged in as a recurring user
        // only recurring users trigger the login mutation, new user triggers register mutation
        // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
        // if you change logic here, also change it in login page
        const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
        if (shouldMergeBasket) {
            try {
                mergeBasket.mutate({
                    headers: {
                        // This is not required since the request has no body
                        // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                        'Content-Type': 'application/json'
                    },
                    parameters: {
                        createDestinationBasket: true
                    }
                })
            } catch (e) {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage(API_ERROR_MESSAGE)
                })
            }
        }
    }

    const submitForm = async (data) => {
        if (isIPBlocked) {
            const path = buildUrl(isOutletSite ? OUTLET_DISPATCH_URL : '/')
            history.push(path)
            return
        }

        const isXssDetected = validateXssForm(data)
        if (isXssDetected) {
            return
        }

        form.clearErrors()

        const handlePasswordlessLogin = async (email) => {
            try {
                await authorizePasswordlessLogin.mutateAsync({userid: email})
                setCurrentView(EMAIL_VIEW)
            } catch (error) {
                const message = USER_NOT_FOUND_ERROR.test(error.message)
                    ? formatMessage(CREATE_ACCOUNT_FIRST_ERROR_MESSAGE)
                    : PASSWORDLESS_ERROR_MESSAGES.some((msg) => msg.test(error.message))
                    ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                    : formatMessage(API_ERROR_MESSAGE)
                form.setError('global', {type: 'manual', message})
            }
        }

        return {
            login: async (data) => {
                if (loginType === LOGIN_TYPES.PASSWORD) {
                    try {
                        const updatedData = removePrefixFromKeys(data, loginFormPrefix)
                        await login.mutateAsync({
                            username: updatedData.email,
                            password: updatedData.password
                        })
                        /* GTM Integration */
                        triggerLoginEvent()

                        await updateShopperContextAfterLogout()
                    } catch (error) {
                        const message = /Unauthorized/i.test(error.message)
                            ? formatMessage(LOGIN_ERROR_MESSAGE)
                            : formatMessage(API_ERROR_MESSAGE)
                        form.setError('global', {type: 'manual', message})
                    }
                    handleMergeBasket()
                } else if (loginType === LOGIN_TYPES.PASSWORDLESS) {
                    setPasswordlessLoginEmail(data.email)
                    await handlePasswordlessLogin(data.email)
                }
            },
            email: async () => {
                await handlePasswordlessLogin(passwordlessLoginEmail)
            }
        }[currentView](data)
    }

    // Handles passwordless login by retrieving the 'token' from the query parameters and
    // executing a passwordless login attempt using the token. The process waits for the
    // customer baskets to be loaded to guarantee proper basket merging.
    useEffect(() => {
        if (path === PASSWORDLESS_LOGIN_LANDING_PATH && isSuccessCustomerBaskets) {
            const token = decodeURIComponent(queryParams.get('token'))
            if (queryParams.get('redirect_url')) {
                setRedirectPath(decodeURIComponent(queryParams.get('redirect_url')))
            } else {
                setRedirectPath('')
            }

            const passwordlessLogin = async () => {
                try {
                    await loginPasswordless.mutateAsync({pwdlessLoginToken: token})
                } catch (e) {
                    const errorData = await e.response?.json()
                    const message = INVALID_TOKEN_ERROR.test(errorData.message)
                        ? formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                        : formatMessage(API_ERROR_MESSAGE)
                    form.setError('global', {type: 'manual', message})
                }
            }
            passwordlessLogin()
        }
    }, [path, isSuccessCustomerBaskets])

    // If customer is registered push to account page and merge the basket
    useEffect(() => {
        if (isRegistered) {
            handleMergeBasket()
            const redirectTo = redirectPath ? redirectPath : '/account'
            navigate(redirectTo)
        }
    }, [isRegistered, redirectPath])

    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(location.pathname)
    }, [])
    return (
        <Box data-testid="login-page" layerStyle="userpage">
            <Seo
                title={formatMessage({
                    id: 'login_page.seo.title',
                    defaultMessage: 'Sign in'
                })}
                description={formatMessage({
                    id: 'login_page.seo.desc',
                    defaultMessage: 'Customer sign in'
                })}
            />
            <Container px={[16, 16, 24, 24]} py={24} width={['100%']} maxW="400px" bg="white">
                <Stack spacing={32}>
                    <Heading as="h1" size="4xl" textTransform="uppercase">
                        <FormattedMessage defaultMessage="Login" id="login_page.heading" />
                    </Heading>

                    {!form.formState.isSubmitSuccessful && currentView === LOGIN_VIEW && (
                        <LoginForm
                            form={form}
                            submitForm={submitForm}
                            clickCreateAccount={() => navigate('/registration')}
                            handlePasswordlessLoginClick={() => {
                                setLoginType(LOGIN_TYPES.PASSWORDLESS)
                            }}
                            handleForgotPasswordClick={() => navigate('/reset-password')}
                            isPasswordlessEnabled={isPasswordlessEnabled}
                            isSocialEnabled={isSocialEnabled}
                            idps={idps}
                            setLoginType={setLoginType}
                        />
                    )}
                    {form.formState.isSubmitSuccessful && currentView === EMAIL_VIEW && (
                        <PasswordlessEmailConfirmation
                            form={form}
                            submitForm={submitForm}
                            email={passwordlessLoginEmail}
                        />
                    )}
                </Stack>
            </Container>
        </Box>
    )
}

Login.getTemplateName = () => 'login'

Login.propTypes = {
    initialView: PropTypes.oneOf([LOGIN_VIEW, EMAIL_VIEW]),
    match: PropTypes.object
}

export default Login
